import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import CalendarFilter from './RecruitmentCalendarFilter';
import useUsers from '../../context/UsersContext';
import CalendarDialog from './RecruitmentCalendarDialog';
import recruitmentCalendarService from '../../service/recruitmentCalendar.service';
import resourceOneService from '../../service/resources/resourceOne.service';
import { Tooltip } from '@mui/material';

export const RecruitmentCalendarPage = () => {
  const { user, getUser } = useUsers();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState({ days: [] });
  const [dateRange, setDateRange] = useState({});
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);
  const [singleCalendarDay, setSingleCalendarDay] = useState({});

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    if (dataChanged) {
      getAllCalendarDays();
      setDataChanged(false);
    }
  }, [dataChanged]);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (calendarApi.view.type !== 'timeGridDay') {
        calendarApi.changeView('timeGridDay', date.startDate.toDate());
      } else {
        calendarApi.gotoDate(date.startDate.toDate());
      }
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null) {
      setDateRange(date);
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && user.userId) {
      getAllCalendarDays();
    }
  }, [dateRange.startDate, dateRange.endDate, user.userId]);

  const calendarViews = {
    dayGridMonth: {
      dayHeaderFormat: { weekday: 'long' },
    },
    timeGridWeek: {
      dayHeaderFormat: { month: 'long', day: 'numeric', weekday: 'long' },
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    timeGridDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
  };

  const getResourceOneById = (id) => {
    resourceOneService.getResourceById(id).then((data) => {
      setSingleCalendarDay(data);
      setCalendarDialogOpen(true);
    });
  };

  const getAllCalendarDays = () => {
    recruitmentCalendarService.getCalendarDays(dateRange).then((data) => {
      setEvents((prev) => ({
        ...prev,
        days: data,
      }));
    });
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (dateRange.startDate?.isSame(startDate, 'day') && dateRange.endDate?.isSame(endDate, 'day')) {
      return;
    }
    setDateRange({ startDate, endDate });
  };

  return (
    <div className="calendarView flex bg-[#f3f4f6]">
      <div className="w-[350px]">
        <CalendarFilter dates={{ date, setDate }} />
      </div>
      <div className="w-full bg-white">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next,today',
            center: 'title',
            right: 'timeGridDay,timeGridWeek,dayGridMonth',
          }}
          locales={[huLocale]}
          views={calendarViews}
          events={[...events.days]}
          datesSet={handleDatesSet}
          nowIndicator={true}
          eventClick={(clickInfo) => {
            if (clickInfo.event.display !== 'background') {
              const id = clickInfo.event?.extendedProps?.resourceId;
              getResourceOneById(id);
            }
          }}
          eventContent={(eventInfo) => {
            return (
              <Tooltip
                arrow
                placement="top"
                title={eventInfo.event.extendedProps?.message}
                slotProps={{
                  tooltip: {
                    sx: {
                      color: '#514E6A',
                      backgroundColor: '#ffff',
                      boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                      maxWidth: '300px',
                      fontSize: '0.9rem',
                    },
                  },
                  arrow: {
                    sx: {
                      color: '#ffff',
                    },
                  },
                }}
              >
                <div className="truncate text-white font-bold w-full mt-1">{eventInfo.event.title}</div>
              </Tooltip>
            );
          }}
        />
      </div>
      <CalendarDialog
        changed={{ dataChanged, setDataChanged }}
        open={calendarDialogOpen}
        handleClose={() => {
          setCalendarDialogOpen(false);
        }}
        data={singleCalendarDay}
      />
    </div>
  );
};
